import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  BloodResult,
  AnalysesTypesLowercase,
  MeasurementResult,
  AnalysesTypes,
} from 'src/app/analyses/interfaces/analyses.types';
import { Router } from '@angular/router';
import { AnalysisLink, MicrobiomeLink, ReportLink } from '../../shared.types';
import { resolveTypePath } from 'src/app/analyses/util/link-util';
import { AnalysisId } from 'src/app/analyses/interfaces/analysis-id.enum';
import { DecimalPlacesPipe } from '../../pipes';
import { SemanticType } from '../../constants/semantic-type';

@Component({
  selector: 'app-analysis-link',
  templateUrl: './analysis-link.component.html',
  styleUrls: ['./analysis-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisLinkComponent implements OnInit {
  @Input() analysis: AnalysisLink; // Previously AnalysisLink
  @Input() microbiome: MicrobiomeLink;
  @Input() report: ReportLink;
  @Input() customClickEvent = false;
  @Input() hideCircles = false;
  @Input() type: AnalysesTypesLowercase;
  @Input() showNoResult = true;
  @Input() withLink = true;
  @Input() reCheckLabel = false;
  @Output() clicked = new EventEmitter<AnalysisLink>();

  public hasResult = false;

  AnalysesTypes = AnalysesTypes;
  AnalysisId = AnalysisId;
  AnalysesTypesLowercase = AnalysesTypesLowercase;
  SemanticTypes = SemanticType;
  resultLabel = '';

  constructor(
    private readonly router: Router,
    private decimalPlacesPipe: DecimalPlacesPipe
  ) {}

  ngOnInit(): void {
    // Backend will sometimes return 'No result' when there isn't one,
    // and sometimes nothing at all.
    if (this.analysis) {
      this.hasResult =
        this.analysis?.result && this.analysis.result !== 'No result';
      this.resolveResultLabel(this.analysis);
    }
  }

  onClick() {
    if (this.customClickEvent) {
      this.clicked.emit();
      return;
    }

    if (this.report) {
      const type = resolveTypePath(this.report.type);
      if (type === AnalysesTypesLowercase.PRS) {
        return this.router.navigate([`/prs/results/${this.report.id}`]);
      } else if (type === AnalysesTypesLowercase.NIPT) {
        return this.router.navigate(['/nipt/results', this.report.id]);
      } else if (type === AnalysesTypesLowercase.Blood) {
        return this.router.navigate([`/blood/results/${this.report.id}`]);
      } else {
        if (this.report.type === AnalysesTypes.DNALifestyle) {
          return this.router.navigate(['/dna-snp/results', this.report.id]);
        }
        return this.router.navigate(['/dna/results', this.report.id]);
      }
    }

    if (this.hasResult) {
      const resolvedType = resolveTypePath(this.type);
      if (resolvedType === AnalysesTypesLowercase.PRS) {
        this.router.navigate([`/prs/analyses/`, this.analysis?.id]);
      } else if (resolvedType === AnalysesTypesLowercase.Habits) {
        // TODO: Verify this
        // Two different API calls will return id in two ways:
        // - On /habits we will get analysisId
        // - On /results/factor/1234 you will get id
        const id = this.analysis.id || this.analysis.analysisId;
        this.router.navigate([
          '/',
          resolvedType,
          'analyses',
          id,
          this.analysis.calculationId,
        ]);
      } else if (resolvedType === AnalysesTypesLowercase.Ancestry) {
        this.router.navigate([
          this.router.url + '/' + this.analysis?.analysisId,
        ]);
      } else if (resolvedType === AnalysesTypesLowercase.PhysicalTraits) {
        this.router.navigate([
          'ancestry/physical-traits/analyses/' + this.analysis?.analysisId,
        ]);
      } else {
        const resultId =
          this.analysis.resultId || // panel
          this.analysis.calculationId || // related analyses
          (this.analysis.result as BloodResult).id; // result view
        const analysisId =
          this.analysis.analysisId || // everywhere except blood panel
          this.analysis?.id; // blood panel
        const url = [
          '/',
          this.analysis.type === AnalysesTypes.DNA ? 'dna-snp' : resolvedType,
          'analyses',
          analysisId,
        ];

        if (this.type !== AnalysesTypesLowercase.DNA) {
          url.push(resultId);
        }

        this.router.navigate(url);
      }
    } else if (this.analysis?.type === AnalysesTypesLowercase.DNA) {
      // TODO unlock
    } else {
      if (this.analysis.analysisId === AnalysisId.stepsId) {
        return this.router.navigate(['/habits/missing-steps']);
      }

      this.router.navigate([
        '/',
        this.type,
        'analyses',
        this.analysis?.analysisId || this.analysis.id,
        'result',
        'add',
      ]);
    }
  }

  resolveResultLabel(analysis: AnalysisLink) {
    const { result, analysisId } = analysis;
    if (!result) {
      return;
    }

    if (typeof result === 'string') {
      // if habits
      this.resultLabel = result;
    } else if (Array.isArray(analysis?.result)) {
      // no decimal places for imperial unit system body weight & height
      if (
        (analysisId === AnalysisId.BodyHeight &&
          (result as MeasurementResult[]).length > 1) ||
        (analysisId === AnalysisId.BodyWeight && result[0].unitName === 'lbs')
      ) {
        (result as MeasurementResult[]).forEach((res) => {
          this.resultLabel += `${res.result} ${res.unitName} `;
        });
      }
      // if measurement (blood pressure and height in metric system returns two items in array)
      else if ((result as MeasurementResult[]).length > 1) {
        if (analysisId == AnalysisId.BloodPressure) {
          this.resultLabel = `
          ${result[0].result}/${result[1].result} ${result[0].unitName}`;
        }
      } else if (analysisId == AnalysisId.HeartRate) {
        this.resultLabel = `${result[0].result} ${result[0].unitName || ''}`;
      } else {
        this.resultLabel = `${this.decimalPlacesPipe.transform(
          result[0].result
        )} ${result[0].unitName || ''}`;
      }
    } else if (typeof result === 'object') {
      // if blood
      const bloodResult = result as BloodResult;
      this.resultLabel = `${this.decimalPlacesPipe.transform(
        bloodResult.value
      )} ${
        bloodResult.unit.isVisibleResult && bloodResult.unit.name
          ? bloodResult.unit.name
          : ''
      }`;
    }

    this.resultLabel = this.resultLabel
      ? this.resultLabel.replace('Proportion of 1.0', '') // R&D asked to hide this "unit", because it is not a real unit.
      : '';
  }

  displayReCheck(date: string) {
    const differenceInTime = new Date().getTime() - new Date(date).getTime();
    const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

    return differenceInDays > 330;
  }
}
